import type { ConditionOperator, ConditionTargetType, FormAnswers, FormAnswerValue, QuestionType } from '../types/forms';

// Fase 23A/23B: espejo EXACTO de la semantica de visibilidad del backend (packages/backend/src/domain/
// forms/formVisibility.ts). Lo usan la Vista Previa del constructor (23A) y el runner de respuestas
// (23B). La seguridad/validacion real la hace el backend; esto es para la experiencia interactiva.
// Mantener ambos en sincronia (mismos casos cubiertos por pruebas en ambos paquetes).

// Forma estructural minima que necesita el evaluador (compatible con FormDraft y con SubmissionDetail).
export interface VisCondition {
  targetType: ConditionTargetType;
  targetQuestionId: string | null;
  targetSectionId: string | null;
  sourceQuestionId: string;
  operator: ConditionOperator;
  valueOptionId: string | null;
  valueNumber: number | null;
  valueText: string | null;
}
export interface VisSection { id: string; position: number }
export interface VisQuestion { id: string; sectionId: string; type: QuestionType; isRequired: boolean; position: number }
export interface VisInput { sections: VisSection[]; questions: VisQuestion[]; conditions: VisCondition[] }

export interface VisibilityResult {
  visibleSectionIds: Set<string>;
  visibleQuestionIds: Set<string>;
  requiredVisibleQuestionIds: Set<string>;
}

const isEmptyText = (value: FormAnswerValue): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const normalizeBool = (value: FormAnswerValue): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'true';
  return false;
};

export function evaluateCondition(condition: VisCondition, sourceAnswer: FormAnswerValue): boolean {
  switch (condition.operator) {
    case 'EQUALS':
      if (condition.valueOptionId != null) return sourceAnswer === condition.valueOptionId;
      if (condition.valueNumber != null) return sourceAnswer != null && Number(sourceAnswer) === condition.valueNumber;
      if (condition.valueText != null) return normalizeBool(sourceAnswer) === (condition.valueText === 'true');
      return false;
    case 'NOT_EQUALS':
      return sourceAnswer !== condition.valueOptionId;
    case 'CONTAINS':
      return Array.isArray(sourceAnswer) && condition.valueOptionId != null && sourceAnswer.includes(condition.valueOptionId);
    case 'NOT_CONTAINS':
      return !(Array.isArray(sourceAnswer) && condition.valueOptionId != null && sourceAnswer.includes(condition.valueOptionId));
    case 'GREATER_THAN':
      return sourceAnswer != null && condition.valueNumber != null && Number(sourceAnswer) > condition.valueNumber;
    case 'GREATER_THAN_OR_EQUAL':
      return sourceAnswer != null && condition.valueNumber != null && Number(sourceAnswer) >= condition.valueNumber;
    case 'LESS_THAN':
      return sourceAnswer != null && condition.valueNumber != null && Number(sourceAnswer) < condition.valueNumber;
    case 'LESS_THAN_OR_EQUAL':
      return sourceAnswer != null && condition.valueNumber != null && Number(sourceAnswer) <= condition.valueNumber;
    case 'IS_EMPTY':
      return isEmptyText(sourceAnswer);
    case 'IS_NOT_EMPTY':
      return !isEmptyText(sourceAnswer);
    default:
      return false;
  }
}

export function evaluateFormVisibility(draft: VisInput, answers: FormAnswers): VisibilityResult {
  const sectionConditions = new Map<string, VisCondition[]>();
  const questionConditions = new Map<string, VisCondition[]>();
  for (const c of draft.conditions) {
    if (c.targetType === 'SECTION' && c.targetSectionId) {
      const list = sectionConditions.get(c.targetSectionId) ?? [];
      list.push(c);
      sectionConditions.set(c.targetSectionId, list);
    } else if (c.targetType === 'QUESTION' && c.targetQuestionId) {
      const list = questionConditions.get(c.targetQuestionId) ?? [];
      list.push(c);
      questionConditions.set(c.targetQuestionId, list);
    }
  }

  const visibleSectionIds = new Set<string>();
  const visibleQuestionIds = new Set<string>();
  const requiredVisibleQuestionIds = new Set<string>();

  const effectiveAnswer = (questionId: string): FormAnswerValue => (visibleQuestionIds.has(questionId) ? answers[questionId] : undefined);
  const allPass = (conds: VisCondition[] | undefined): boolean => !conds || conds.length === 0 || conds.every((c) => evaluateCondition(c, effectiveAnswer(c.sourceQuestionId)));

  const orderedSections = [...draft.sections].sort((a, b) => a.position - b.position);
  const questionsBySection = new Map<string, VisQuestion[]>();
  for (const q of draft.questions) {
    const list = questionsBySection.get(q.sectionId) ?? [];
    list.push(q);
    questionsBySection.set(q.sectionId, list);
  }
  for (const list of questionsBySection.values()) list.sort((a, b) => a.position - b.position);

  for (const section of orderedSections) {
    const sectionVisible = allPass(sectionConditions.get(section.id));
    if (sectionVisible) visibleSectionIds.add(section.id);
    for (const question of questionsBySection.get(section.id) ?? []) {
      const questionVisible = sectionVisible && allPass(questionConditions.get(question.id));
      if (questionVisible) {
        visibleQuestionIds.add(question.id);
        if (question.isRequired) requiredVisibleQuestionIds.add(question.id);
      }
    }
  }

  return { visibleSectionIds, visibleQuestionIds, requiredVisibleQuestionIds };
}
